import { useState, useEffect } from 'react';
import { X, Eye, Clock, User, Shield } from 'lucide-react';
import { patientApi } from '../api';
import type { DocumentAuditLog } from '../types';

function fmtDate(d: string) {
  return new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const ACTION_COLORS: Record<string, string> = {
  VIEW_CERTIFICATE: 'text-blue-600 bg-blue-50',
  VIEW_VAULT_ITEM: 'text-blue-600 bg-blue-50',
  CERTIFICATE_GENERATED: 'text-emerald-600 bg-emerald-50',
  DOCUMENT_SHARED: 'text-purple-600 bg-purple-50',
  SHARE_REVOKED: 'text-red-600 bg-red-50',
  NEOVERIFY_REGISTERED: 'text-teal-600 bg-teal-50',
};

export default function DocumentAccessLogDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [logs, setLogs] = useState<DocumentAuditLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    patientApi.getDocumentAuditLogs()
      .then(setLogs)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white shadow-2xl h-full overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between p-4 border-b border-gray-100 z-10">
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Eye className="w-5 h-5 text-gray-600" /> Document Access Log
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-3">
          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">No access logs yet</div>
          ) : (
            logs.map(log => {
              const actionColor = ACTION_COLORS[log.action] || 'text-gray-600 bg-gray-50';
              return (
                <div key={log.id} className="bg-white border border-gray-100 rounded-xl p-3 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${actionColor}`}>
                      {log.action.replace(/_/g, ' ')}
                    </span>
                    <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                      <Clock className="w-3 h-3" /> {fmtDate(log.performedAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-600">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" /> {log.actorName || 'Unknown'}</span>
                    <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> {log.actorRole || 'SYSTEM'}</span>
                  </div>
                  <p className="text-[10px] text-gray-400">{log.resourceType} / {log.resourceId.substring(0, 12)}...</p>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
